import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@pentabd/ui";

import { CancelButton, ErrorModal, SuccessModal } from "@components";
import { BasicInfo } from "./BasicInfo";
import { LeaveApplyModal } from "./LeaveApplyModal";
import { SupportingDocument } from "./SupportingDocument";

import { yupResolver } from "@hookform/resolvers/yup";
import { useGeneralInfo } from "@hooks/HRM/employee/useGeneralInfo";
import { useLeaveSummary } from "@hooks/HRM/leave/useLeaveSummary";
import { useFunctionalityData } from "@hooks/useFunctionalityData";
import { useMasterData } from "@hooks/useMasterData";
import { useUser } from "@hooks/useUser";

import { FUNCTIONALITY_DATA } from "@constants/functionality-data";
import { MASTER_DATA } from "@constants/master-data";
import { ROUTES } from "@constants/routes";
import { STATUS } from "@constants/status.hrm";
import { TABS } from "@constants/tab-serial";

import { GeneralInfoType } from "@type/HRM/general-info";
import { LeaveApplyType } from "@type/HRM/leave-apply";

import { convertDocumentData } from "@utils/documentDataConvert";
import { calculateDaysDifference } from "@utils/duration";
import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

import { applyLeave, sendLeaveForApproval } from "@api/leave-apply";

import {
  ApplyLeaveFormDataType,
  SEND_FOR_APPROVAL,
  SUPPORTING_DOCUMENT,
  applyLeaveFormValidation,
} from "@validations/HRM/leave/applyLeaveFormValidation";

const {
  HRM: { PROFILE },
} = ROUTES;
const { APPROVAL_MATRIX, LEAVE_TYPE } = FUNCTIONALITY_DATA;
const { SUCCESS, FAILED } = STATUS;

export const LeaveApplyBody = () => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isApplyBtnDisabled, setIsApplyBtnDisabled] = useState<boolean>(false);
  const [isSendForApprovalModalOpen, setIsSendForApprovalModalOpen] =
    useState<boolean>(false);
  const [leaveDraftData, setLeaveDraftData] = useState<ApplyLeaveFormDataType>(
    {} as ApplyLeaveFormDataType,
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const methods = useForm<ApplyLeaveFormDataType>({
    resolver: yupResolver(applyLeaveFormValidation),
  });

  const { user } = useUser();
  const { employeeMasterId, unitId, gradeId } = user || {};
  const { generalInfo } = useGeneralInfo(employeeMasterId);
  const { data: leaveType } = useFunctionalityData(LEAVE_TYPE);

  const { data: designation } = useFunctionalityData(APPROVAL_MATRIX);

  const { data: leaveNature } = useMasterData(MASTER_DATA.LEAVE_NATURE);
  const { leaveBalance } = useLeaveSummary(employeeMasterId as string);

  const filteredLeaveTypes = leaveType?.filter((leaveType: any) => {
    const matchingLeave = leaveBalance?.find(
      (balance) => balance.leaveTypeId === leaveType.id,
    );

    leaveType.maxDuration = matchingLeave?.leaveBalanceCount;
    return matchingLeave && matchingLeave.leaveBalanceCount > 0;
  });

  const fileOnHand = methods.watch(SEND_FOR_APPROVAL.FILE_ON_HAND);
  const { joiningDate } = (generalInfo as GeneralInfoType) || {};
  const { employeeName } = GetEmployeeDetails(leaveDraftData?.employee as any);

  const onSubmit: SubmitHandler<ApplyLeaveFormDataType> = (
    data: LeaveApplyType,
  ) => {
    applyLeave(
      {
        ...convertDocumentData(data, SUPPORTING_DOCUMENT),
        applicationDate: dayjs(new Date()).format("YYYY-MM-DD"),
        joiningDate: joiningDate,
        leaveDaysCount: calculateDaysDifference(data.startDate, data.endDate),
      },
      employeeMasterId,
      (e) => {
        if (e.status === SUCCESS) {
          setIsSendForApprovalModalOpen(true);
          setLeaveDraftData(e.data.data);
        } else if (e.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  const handleSendForApproval = () => {
    sendLeaveForApproval(
      {
        leaveDetailsId: leaveDraftData?.id,
        employeeId: leaveDraftData?.employeeMasterId,
        fileOnHand,
      },
      (e) => {
        setIsSendForApprovalModalOpen(false);
        if (e.status === SUCCESS) {
          setIsSuccessModalOpen(true);
        } else if (e.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const closeApprovalModal = () => setIsSendForApprovalModalOpen(false);

  const createSuccessAction = () => {
    navigate(`${PROFILE}?tab=${TABS.LEAVE}`);
    setIsSuccessModalOpen(false);
  };

  useEffect(() => {
    const reset = () => {
      setIsSuccessModalOpen(false);
      setIsErrorModalOpen(false);
      setIsSendForApprovalModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      {/* MODAL SECTION */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      {/* MODAL SECTION */}
      <FormProvider {...methods}>
        <form
          className="container pt-10"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <LeaveApplyModal
            isModalOpen={isSendForApprovalModalOpen}
            onClose={closeApprovalModal}
            employeeName={employeeName}
            designation={designation}
            gradeId={gradeId}
            handleSendForApproval={handleSendForApproval}
          />
          <BasicInfo
            unitId={unitId}
            leaveType={filteredLeaveTypes}
            leaveNature={leaveNature}
            isApplyBtnDisabled={isApplyBtnDisabled}
            setIsApplyBtnDisabled={setIsApplyBtnDisabled}
            employeeId={employeeMasterId}
          />
          <SupportingDocument />
          <div className="row border-top pt-8">
            <div className="col-12 d-flex justify-content-end gap-6">
              <CancelButton />
              <Button
                fill="fill"
                size="sm"
                type="primary"
                htmlType="submit"
                disabled={isApplyBtnDisabled}
              >
                {t("LEAVE_APPLY.FORM.SUBMIT_BUTTON")}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
